import { useState, useRef, useEffect, useMemo, ReactNode } from 'react';
import { Search, Navigation, Loader2 } from 'lucide-react';
import { MapCategoryFilter, MapSpot } from '@/types/spots';

const PhoneBoothIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="6" y="2" width="12" height="20" rx="2" />
    <rect x="10" y="6" width="4" height="14" rx="1" />
    <line x1="6" y1="6" x2="18" y2="6" />
  </svg>
);

const CATEGORY_ICONS: Record<string, ReactNode> = {
  cafe: '☕',
  gym: '🏋️',
  hotel_lobby: '🏨',
  coworking: '💻',
  library: '📚',
  restaurant: '🍽️',
  pod: <PhoneBoothIcon className="w-4 h-4 inline" />,
  other: '✨',
};

interface MapOverlayProps {
  selectedCategory: MapCategoryFilter;
  onCategoryChange: (cat: MapCategoryFilter) => void;
  onSearch: (query: string) => void;
  onUseLocation: () => void;
  isLocating: boolean;
  spotCount: number;
  spots: MapSpot[];
  onSelectSpot: (spot: MapSpot) => void;
}

const MAPBOX_TOKEN = 'pk.eyJ1IjoiemFpbmg0MiIsImEiOiJjbTR1NHBoYjMwZXQ4MmtzYWIwZzJzaWR6In0.gE5QluhHA_G_TzvGpOsRdg';

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

const categories: { value: MapCategoryFilter; label: string; icon: ReactNode }[] = [
  { value: 'all', label: 'All', icon: '📍' },
  { value: 'cafe', label: 'Cafés', icon: '☕' },
  { value: 'hotel_lobby', label: 'Hotels', icon: '🏨' },
  { value: 'library', label: 'Libraries', icon: '📚' },
  { value: 'coworking', label: 'Coworking', icon: '💻' },
  { value: 'gym', label: 'Gyms', icon: '🏋️' },
  { value: 'restaurant', label: 'Restaurants', icon: '🍽️' },
  { value: 'pod', label: 'Pods', icon: <PhoneBoothIcon className="w-3.5 h-3.5" /> },
  { value: 'other', label: 'Other', icon: '✨' },
];

const MapOverlay = ({
  selectedCategory,
  onCategoryChange,
  onSearch,
  onUseLocation,
  isLocating,
  spots,
  onSelectSpot,
}: MapOverlayProps) => {
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [geoResults, setGeoResults] = useState<MapSpot[]>([]);
  const [_isGeoSearching, setIsGeoSearching] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Text-based matches (name/address)
  const textMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return spots.filter(s => {
      const name = s.name?.toLowerCase() || '';
      const addr = (s.short_address || s.formatted_address || '').toLowerCase();
      return name.includes(q) || addr.includes(q);
    }).slice(0, 6);
  }, [query, spots]);

  // Geocode postcodes/areas and find nearby spots
  useEffect(() => {
    const q = query.trim();
    if (!q || q.length < 2) { setGeoResults([]); return; }

    const timer = setTimeout(async () => {
      setIsGeoSearching(true);
      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json?access_token=${MAPBOX_TOKEN}&country=GB&limit=1&types=postcode,neighborhood,locality,place`
        );
        const data = await res.json();
        if (data.features?.[0]) {
          const [lng, lat] = data.features[0].center;
          // Find spots within 3km of this location, sorted by distance
          const nearby = spots
            .filter(s => s.latitude != null && s.longitude != null)
            .map(s => ({ spot: s, dist: haversineKm(lat, lng, s.latitude!, s.longitude!) }))
            .filter(x => x.dist <= 3)
            .sort((a, b) => a.dist - b.dist)
            .slice(0, 8)
            .map(x => x.spot);
          // Only show geo results that aren't already in text matches
          const textIds = new Set(textMatches.map(s => s.id));
          setGeoResults(nearby.filter(s => !textIds.has(s.id)));
        } else {
          setGeoResults([]);
        }
      } catch {
        setGeoResults([]);
      } finally {
        setIsGeoSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, spots, textMatches]);

  const matchedSpots = useMemo(() => {
    const combined = [...textMatches];
    for (const s of geoResults) {
      if (!combined.find(x => x.id === s.id)) combined.push(s);
    }
    return combined.slice(0, 6);
  }, [textMatches, geoResults]);

  const showDropdown = isFocused && query.trim().length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query);
      setIsFocused(false);
    }
  };

  const handleSelect = (spot: MapSpot) => {
    onSelectSpot(spot);
    setQuery('');
    setIsFocused(false);
  };

  return (
    <div className="absolute top-0 left-0 right-0 z-20" style={{ paddingTop: 'env(safe-area-inset-top, 12px)' }}>
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-center gap-2" ref={wrapperRef}>
          <div className="flex-1 relative">
            <form onSubmit={handleSubmit} className="flex items-center bg-background/95 backdrop-blur-xl rounded-full shadow-lg px-4 py-2.5 gap-3">
              <span className="font-funnel font-semibold text-foreground text-sm tracking-tight whitespace-nowrap">qwip spots</span>
              <div className="w-px h-5 bg-border/60 shrink-0" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setIsFocused(true)}
                placeholder="Search area or postcode..."
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none min-w-0"
              />
              <button type="submit" className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-muted/50 transition-colors">
                <Search className="w-4 h-4 text-muted-foreground" />
              </button>
            </form>

            {showDropdown && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background/95 backdrop-blur-xl rounded-2xl shadow-lg overflow-hidden z-30">
                {matchedSpots.length > 0 && (
                  <div className="max-h-[240px] overflow-y-auto">
                    {matchedSpots.map((spot) => (
                      <button
                        key={spot.id}
                        type="button"
                        onClick={() => handleSelect(spot)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
                      >
                        <span className="text-base shrink-0 inline-flex items-center">{CATEGORY_ICONS[spot.category] || '📍'}</span>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">{spot.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{spot.short_address || spot.formatted_address}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {matchedSpots.length > 0 && <div className="border-t border-border/40" />}
                <button
                  type="button"
                  onClick={() => { onSearch(query); setIsFocused(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left"
                >
                  <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-sm text-muted-foreground">Search "{query.trim()}" on map</span>
                </button>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={onUseLocation}
            disabled={isLocating}
            className="shrink-0 w-[42px] h-[42px] flex items-center justify-center bg-background/95 backdrop-blur-xl rounded-full shadow-lg disabled:opacity-50"
          >
            {isLocating ? (
              <Loader2 className="w-4 h-4 text-foreground animate-spin" />
            ) : (
              <Navigation className="w-4 h-4 text-primary" />
            )}
          </button>
        </div>
      </div>

      <div className="px-4 pb-2">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => onCategoryChange(cat.value)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all shadow-sm ${
                selectedCategory === cat.value
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-background/90 backdrop-blur-sm text-foreground hover:bg-background'
              }`}
            >
              <span className="inline-flex items-center">{cat.icon}</span>
              {cat.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MapOverlay;
